import React, { useState, useEffect, useCallback } from "react";
import { DragDropContext } from "react-beautiful-dnd";
import {
    Box,
    Paper,
    Typography,
    Button,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    makeStyles,
    Grid,
    Card,
    CardContent
} from "@material-ui/core";
import { Add as AddIcon, TrendingUp, AttachMoney, Assessment, ChevronLeft, ChevronRight } from "@material-ui/icons";
import { toast } from "react-toastify";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import KanbanColumn from "../../components/KanbanColumn";
import OpportunityModal from "../../components/OpportunityModal";
import TemperatureModal from "../../components/TemperatureModal";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";

import { getFunnels } from "../../services/funnels";
import { getFunnelCards } from "../../services/funnelCards";
import { getFunnelOpportunities, moveOpportunity } from "../../services/funnelOpportunities";

const useStyles = makeStyles((theme) => ({
    mainPaper: {
        flex: 1,
        padding: theme.spacing(2),
        overflowY: "scroll",
        ...theme.scrollbarStyles
    },
    controls: {
        marginBottom: theme.spacing(2),
        display: "flex",
        gap: theme.spacing(2),
        alignItems: "center",
        flexWrap: "wrap"
    },
    funnelSelect: {
        minWidth: 250
    },
    stats: {
        marginBottom: theme.spacing(2)
    },
    statCard: {
        textAlign: "center",
        padding: theme.spacing(2)
    },
    statValue: {
        fontSize: "1.5rem",
        fontWeight: 600,
        color: theme.palette.primary.main
    },
    statLabel: {
        fontSize: "0.85rem",
        color: theme.palette.text.secondary,
        marginTop: theme.spacing(0.5)
    },
    kanbanContainer: {
        position: "relative",
        overflowX: "auto",
        paddingBottom: theme.spacing(2),
        ...theme.scrollbarStyles,
        "&:hover $scrollButton": {
            opacity: 1
        }
    },
    kanbanBoard: {
        display: "flex",
        gap: theme.spacing(2),
        minHeight: 500
    },
    scrollButton: {
        position: "sticky",
        top: "50%",
        transform: "translateY(-50%)",
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        color: "white",
        border: "none",
        borderRadius: "50%",
        width: 48,
        height: 48,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        opacity: 0,
        transition: "opacity 0.3s, background-color 0.2s",
        zIndex: 10,
        flexShrink: 0,
        "&:hover": {
            backgroundColor: "rgba(0, 0, 0, 0.7)"
        },
        "&:disabled": {
            opacity: 0.3,
            cursor: "not-allowed"
        }
    },
    scrollButtonLeft: {
        left: theme.spacing(1),
        marginRight: theme.spacing(-6)
    },
    scrollButtonRight: {
        right: theme.spacing(1),
        marginLeft: theme.spacing(-6)
    }
}));

const KanbanBoard = () => {
    const classes = useStyles();

    const [funnels, setFunnels] = useState([]);
    const [selectedFunnelId, setSelectedFunnelId] = useState(null);
    const [cards, setCards] = useState([]);
    const [opportunities, setOpportunities] = useState([]);
    const [opportunityModalOpen, setOpportunityModalOpen] = useState(false);
    const [selectedCardId, setSelectedCardId] = useState(null);
    const [loading, setLoading] = useState(false);
    const kanbanContainerRef = React.useRef(null);

    // Temperature modal state
    const [temperatureModalOpen, setTemperatureModalOpen] = useState(false);
    const [pendingMove, setPendingMove] = useState(null);

    const fetchFunnels = useCallback(async () => {
        try {
            const { data } = await getFunnels();
            setFunnels(data);
            if (data.length > 0 && !selectedFunnelId) {
                setSelectedFunnelId(data[0].id);
            }
        } catch (err) {
            toastError(err);
        }
    }, [selectedFunnelId]);

    const fetchCards = useCallback(async () => {
        if (!selectedFunnelId) return;
        try {
            const { data } = await getFunnelCards(selectedFunnelId);
            setCards(data);
        } catch (err) {
            toastError(err);
        }
    }, [selectedFunnelId]);

    const fetchOpportunities = useCallback(async () => {
        if (!selectedFunnelId) return;
        try {
            const { data } = await getFunnelOpportunities(selectedFunnelId);
            setOpportunities(data);
        } catch (err) {
            toastError(err);
        }
    }, [selectedFunnelId]);

    useEffect(() => {
        fetchFunnels();
    }, [fetchFunnels]);

    useEffect(() => {
        if (selectedFunnelId) {
            fetchCards();
            fetchOpportunities();
        }
    }, [selectedFunnelId, fetchCards, fetchOpportunities]);

    const handleDragEnd = async (result) => {
        const { destination, source, draggableId } = result;

        // Dropped outside
        if (!destination) return;

        // Dropped in same position
        if (
            destination.droppableId === source.droppableId &&
            destination.index === source.index
        ) {
            return;
        }

        const opportunityId = parseInt(draggableId);
        const toCardId = parseInt(destination.droppableId);
        const fromCardId = parseInt(source.droppableId);

        // Find cards and opportunity for modal
        const fromCard = cards.find(c => c.id === fromCardId);
        const toCard = cards.find(c => c.id === toCardId);
        const opportunity = opportunities.find(opp => opp.id === opportunityId);

        // Calculate suggested temperature based on toCard percentage
        let suggestedTemperature = "cold";
        if (toCard.percentage) {
            if (toCard.percentage < 25) {
                suggestedTemperature = "cold";
            } else if (toCard.percentage >= 25 && toCard.percentage <= 50) {
                suggestedTemperature = "warm";
            } else {
                suggestedTemperature = "hot";
            }
        }

        // Store pending move and open temperature modal
        setPendingMove({
            opportunityId,
            toCardId,
            fromCard,
            toCard,
            opportunity,
            suggestedTemperature
        });
        setTemperatureModalOpen(true);
    };

    const handleTemperatureConfirm = async ({ temperature, notes }) => {
        if (!pendingMove) return;

        const { opportunityId, toCardId } = pendingMove;

        // Optimistic update
        const updatedOpportunities = opportunities.map(opp => {
            if (opp.id === opportunityId) {
                const newCard = cards.find(c => c.id === toCardId);
                return { ...opp, funnelCardId: toCardId, card: newCard };
            }
            return opp;
        });
        setOpportunities(updatedOpportunities);

        // API call with temperature and notes
        try {
            await moveOpportunity(opportunityId, toCardId, temperature, notes);
            toast.success(i18n.t("kanban.board.toasts.moved"));
            fetchOpportunities(); // Refresh to get updated data
        } catch (err) {
            toastError(err);
            // Revert on error
            fetchOpportunities();
        }

        setPendingMove(null);
    };

    const handleAddOpportunity = (cardId) => {
        setSelectedCardId(cardId);
        setOpportunityModalOpen(true);
    };

    const getOpportunitiesByCard = (cardId) => {
        return opportunities.filter(opp => opp.funnelCardId === cardId);
    };

    const calculateStats = () => {
        const total = opportunities.length;
        const totalValue = opportunities.reduce((sum, opp) => sum + (parseFloat(opp.value) || 0), 0);

        // Contar oportunidades convertidas
        const won = opportunities.filter(opp => opp.status === "won").length;

        // Taxa de conversão = (convertidas / total) * 100
        const conversionRate = total > 0 ? ((won / total) * 100).toFixed(1) : 0;

        return { total, totalValue, conversionRate };
    };

    const stats = calculateStats();

    return (
        <MainContainer>
            <OpportunityModal
                open={opportunityModalOpen}
                onClose={() => {
                    setOpportunityModalOpen(false);
                    setSelectedCardId(null);
                }}
                funnelId={selectedFunnelId}
                reload={fetchOpportunities}
            />

            <TemperatureModal
                open={temperatureModalOpen}
                onClose={() => {
                    setTemperatureModalOpen(false);
                    setPendingMove(null);
                }}
                onConfirm={handleTemperatureConfirm}
                fromCard={pendingMove?.fromCard}
                toCard={pendingMove?.toCard}
                opportunityTitle={pendingMove?.opportunity?.title}
                suggestedTemperature={pendingMove?.suggestedTemperature}
            />

            <MainHeader>
                <Title>{i18n.t("kanban.board.title")}</Title>
                <MainHeaderButtonsWrapper>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => handleAddOpportunity(null)}
                        startIcon={<AddIcon />}
                    >
                        {i18n.t("kanban.board.buttons.newOpportunity")}
                    </Button>
                </MainHeaderButtonsWrapper>
            </MainHeader>

            <Paper className={classes.mainPaper} variant="outlined">
                <Box className={classes.controls}>
                    <FormControl variant="outlined" className={classes.funnelSelect} size="small">
                        <InputLabel>{i18n.t("kanban.board.selectFunnel")}</InputLabel>
                        <Select
                            value={selectedFunnelId || ""}
                            onChange={(e) => setSelectedFunnelId(e.target.value)}
                            label={i18n.t("kanban.board.selectFunnel")}
                        >
                            {funnels.map((funnel) => (
                                <MenuItem key={funnel.id} value={funnel.id}>
                                    {funnel.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>

                <Grid container spacing={2} className={classes.stats}>
                    <Grid item xs={12} sm={4}>
                        <Card className={classes.statCard} elevation={2}>
                            <CardContent>
                                <TrendingUp color="primary" />
                                <Typography className={classes.statValue}>
                                    {stats.total}
                                </Typography>
                                <Typography className={classes.statLabel}>
                                    {i18n.t("kanban.board.stats.totalOpportunities")}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <Card className={classes.statCard} elevation={2}>
                            <CardContent>
                                <AttachMoney color="primary" />
                                <Typography className={classes.statValue}>
                                    {new Intl.NumberFormat("pt-BR", {
                                        style: "currency",
                                        currency: "BRL",
                                        minimumFractionDigits: 0
                                    }).format(stats.totalValue)}
                                </Typography>
                                <Typography className={classes.statLabel}>
                                    {i18n.t("kanban.board.stats.totalValue")}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <Card className={classes.statCard} elevation={2}>
                            <CardContent>
                                <Assessment color="primary" />
                                <Typography className={classes.statValue}>
                                    {stats.conversionRate}%
                                </Typography>
                                <Typography className={classes.statLabel}>
                                    {i18n.t("kanban.board.stats.conversionRate")}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                <Box className={classes.kanbanContainer} ref={kanbanContainerRef}>
                    <DragDropContext onDragEnd={handleDragEnd}>
                        <Box className={classes.kanbanBoard}>
                            <button
                                className={`${classes.scrollButton} ${classes.scrollButtonLeft}`}
                                onClick={() => {
                                    if (kanbanContainerRef.current) {
                                        kanbanContainerRef.current.scrollBy({ left: -400, behavior: 'smooth' });
                                    }
                                }}
                                aria-label="Scroll left"
                            >
                                <ChevronLeft fontSize="large" />
                            </button>
                            {cards.map((card) => (
                                <KanbanColumn
                                    key={card.id}
                                    card={card}
                                    opportunities={getOpportunitiesByCard(card.id)}
                                    onAddOpportunity={handleAddOpportunity}
                                    onUpdate={fetchOpportunities}
                                />
                            ))}
                            <button
                                className={`${classes.scrollButton} ${classes.scrollButtonRight}`}
                                onClick={() => {
                                    if (kanbanContainerRef.current) {
                                        kanbanContainerRef.current.scrollBy({ left: 400, behavior: 'smooth' });
                                    }
                                }}
                                aria-label="Scroll right"
                            >
                                <ChevronRight fontSize="large" />
                            </button>
                        </Box>
                    </DragDropContext>
                </Box>
            </Paper>
        </MainContainer>
    );
};

export default KanbanBoard;
